import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {MIN_PRIMER_LENGTH} from 'biojs/SequencePrimer';
import {MisprimeOptions} from 'molbio/misprime/MisprimeUtils';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import NumericInput from 'ui-library/inputs/NumericInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {DEGREE_SIGN, LESS_OR_EQUAL_SIGN} from 'util/UnicodeSymbols';

export interface MisprimeParamsProps {
  displayTextPrefix?: string;
  misprimeParams: MisprimeOptions;
  setMisprimeParams: (params: MisprimeOptions) => void;
  showExactAndUniqueMatch?: boolean;
}

function getMismatchText(numMismatches: number): string {
  if (numMismatches === 0) {
    return 'separated by no mismatches';
  } else {
    return `separated by ${LESS_OR_EQUAL_SIGN} ${numMismatches} ${pluralize(numMismatches, 'mismatch')}`;
  }
}

export default function MisprimeParams(props: MisprimeParamsProps): JSX.Element {
  const {misprimeParams, setMisprimeParams} = props;

  return (
    <>
      <div className={css(styles.row)}>
        <span>{`${props.displayTextPrefix ? `${props.displayTextPrefix} ` : ''}with at least `}</span>
        <NumericInput
          className={`min-matching-bases ${css(styles.fixedSizeInput)}`}
          value={misprimeParams.minMatchingBases}
          onChange={(e) => {
            setMisprimeParams({...misprimeParams, minMatchingBases: e.target.numericValue});
          }}
          min={MIN_PRIMER_LENGTH}
        />
        <span> matching bases</span>
      </div>
      <div className={css(styles.row)}>
        <SelectDropdown
          displayName={getMismatchText(misprimeParams.maxMismatches)}
          menu={
            <Menu value={misprimeParams.maxMismatches}>
              {[5, 4, 3, 2, 1, 0].map((maxMismatchOption: number) => (
                <Option
                  key={maxMismatchOption}
                  value={maxMismatchOption}
                  onSelect={() => {
                    setMisprimeParams({...misprimeParams, maxMismatches: maxMismatchOption});
                  }}
                >
                  {getMismatchText(maxMismatchOption)}
                </Option>
              ))}
            </Menu>
          }
        />
      </div>
      <div className={css(styles.row)}>
        <span>{`${props.displayTextPrefix ? `${props.displayTextPrefix} with ` : 'and '}no more than `}</span>
        <NumericInput
          className={css(styles.fixedSizeInput)}
          value={misprimeParams.maxTotalMismatches}
          onChange={(e) => {
            setMisprimeParams({...misprimeParams, maxTotalMismatches: e.target.numericValue});
          }}
        />
        <span> total mismatches</span>
      </div>
      <div className={css(styles.row)}>
        <span>
          with{' '}
          <abbr title="melting temperature">
            T<sub>m</sub>
          </abbr>{' '}
          above
        </span>
        <NumericInput
          className={css(styles.fixedSizeInput)}
          value={misprimeParams.minMeltingTemp}
          onChange={(e) => {
            setMisprimeParams({...misprimeParams, minMeltingTemp: e.target.numericValue});
          }}
        />
        <span>{`${DEGREE_SIGN}C and under `}</span>
        <NumericInput
          className={css(styles.fixedSizeInput)}
          value={misprimeParams.maxMeltingTemp}
          onChange={(e) => {
            setMisprimeParams({...misprimeParams, maxMeltingTemp: e.target.numericValue});
          }}
        />
        <span>{`${DEGREE_SIGN}C.`}</span>
      </div>
      {props.showExactAndUniqueMatch && (
        <>
          <div className={css(styles.row)}>
            <CheckboxInput
              checked={misprimeParams.exactMatch ?? false}
              id="misprime-exact-match"
              label="where entire primer matches"
              onChange={() => {
                setMisprimeParams({...misprimeParams, exactMatch: !misprimeParams.exactMatch});
              }}
            />
          </div>
          <div className={css(styles.row)}>
            <CheckboxInput
              checked={misprimeParams.uniqueMatch ?? false}
              id="misprime-unique-match"
              label="where primer matches exactly once on the sequence"
              onChange={() => {
                setMisprimeParams({...misprimeParams, uniqueMatch: !misprimeParams.uniqueMatch});
              }}
            />
          </div>
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    whiteSpace: 'pre-wrap', // Stops Flexbox from stripping trailing spaces from elements
  },
  fixedSizeInput: {
    flex: '0 0 40px',
  },
});
